import React from 'react';
import { Download, Eye, FileText, Folder, Star } from 'lucide-react';

import SixDetailCloneStatusIndicator from '@/components/organism/SixDetail/SixDetailCloneStatusIndicator';
import type { SixDetailFileBrowserRowProps } from '@/components/organism/SixDetail/SixDetailFileBrowser/SixDetailFileBrowser.types';
import SixDetailFileBrowserRowActions from '@/components/organism/SixDetail/SixDetailFileBrowser/SixDetailFileBrowserRowActions';
import { useFileCanDownload } from '@/hooks/poc/useFileCloneStatus';
import { useSixDetailFileBrowserRowDnd } from '@/hooks/sixDetail/useSixDetailFileBrowserRowDnd';
import { classNames } from '@/utils/classNames.utils';

import { getCreatedByLabel, getPathLabel } from './fileBrowserRow.utils';

function SixDetailFileBrowserMobileRow({
  item,
  showPathColumn,
  isSelected,
  canShowRowActions,
  onItemAction,
  onToggleSelect,
  selectedIds,
  isDragDisabled,
}: SixDetailFileBrowserRowProps): React.ReactElement {
  const canDownload = useFileCanDownload(item);
  const {
    attributes,
    listeners,
    setRowRef,
    isDragging,
    isOver,
    isFolder,
    isRowDragDisabled,
  } = useSixDetailFileBrowserRowDnd({
    item,
    isSelected,
    selectedIds,
    isDragDisabled,
    dragIdPrefix: 'mobile-drag:',
    droppableIdPrefix: 'mobile-',
  });

  function handleRowClick(): void {
    if (item.kind === 'folder') {
      onItemAction(item, 'open');
      return;
    }

    onToggleSelect(item.id);
  }

  return (
    <div
      ref={setRowRef}
      {...attributes}
      {...listeners}
      className={classNames(
        'rounded-lg border p-4 transition-colors',
        isSelected ? 'border-primary/30 bg-primary/5' : 'border-border bg-background hover:bg-muted/30',
        !isRowDragDisabled && 'touch-action-none',
        isDragging && 'opacity-40',
        isOver && isFolder && 'bg-primary/10 ring-1 ring-primary/40',
      )}
      onClick={handleRowClick}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            {item.kind === 'folder' ? (
              <Folder className="h-5 w-5 flex-shrink-0 text-primary" />
            ) : (
              <FileText className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
            )}
            <span className="truncate text-sm font-medium text-foreground">{item.name}</span>
          </div>
          {item.kind === 'file' && (
            <div className="mt-2">
              <SixDetailCloneStatusIndicator file={item} />
            </div>
          )}
          <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <span>{item.size ?? '-'}</span>
            <span>•</span>
            <span>{item.modified}</span>
          </div>
          {showPathColumn && (
            <div className="mt-1 text-xs text-muted-foreground">
              Path: {getPathLabel(item)}
            </div>
          )}
          <div className="mt-1 text-xs text-muted-foreground">
            Added by {getCreatedByLabel(item)}
          </div>
        </div>

        <div
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <SixDetailFileBrowserRowActions
            item={item}
            canShowRowActions={canShowRowActions}
            onItemAction={onItemAction}
          />
        </div>
      </div>

      {item.kind === 'file' && (
        <div
          className="mt-3 flex items-center gap-1"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            onClick={() => {
              onItemAction(item, 'favorite');
            }}
          >
            <Star className={classNames('h-3.5 w-3.5', item.isFavorite === true && 'fill-current text-amber-500')} />
            Favorite
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            onClick={() => {
              onItemAction(item, 'view');
            }}
          >
            <Eye className="h-3.5 w-3.5" />
            View
          </button>
          <button
            type="button"
            className={classNames(
              'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors',
              canDownload
                ? 'text-muted-foreground hover:bg-muted hover:text-foreground'
                : 'cursor-not-allowed text-muted-foreground/50',
            )}
            disabled={!canDownload}
            onClick={() => {
              if (canDownload) {
                onItemAction(item, 'download');
              }
            }}
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </button>
        </div>
      )}
    </div>
  );
}

export default SixDetailFileBrowserMobileRow;
